import { useEffect, useRef, useState } from "react"
import "./index.scss"

export type AssociationInputProps = {
  onSend: (value: string) => void
  defalutValue: string
  /**
   * 联想函数，根据输入(可为空)展示联想结果,当输入不为空时，弹出层中匹配的字符串匹配部分标注
   */
  associationFn: (value: string) => Promise<string[]>
  status: "loading" | "error" | "normal" | "disabled"
}

const HighlightMatch = ({ text, query }: { text: string; query: string }) => {
  if (!query) return <>{text}</>

  const regex = new RegExp(`(${query})`, "gi")
  const parts = text.split(regex)

  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span key={i} className="highlight">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </>
  )
}

const DEBOUNCE_DELAY = 5e2

export function AssociationInput(props: AssociationInputProps) {
  const [value, setValue] = useState("")
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<NodeJS.Timeout>()

  const getSuggestions = async () => {
    try {
      const history = await props.associationFn(value)
      setSuggestions(history)
    } catch (error) {
      console.error(error)
    }
  }

  const handleSelect = (suggestion: string) => {
    setValue(suggestion)
    setShowSuggestions(false)
    const input = containerRef.current?.querySelector("input")
    input?.focus()
  }

  const handleSend = () => {
    const sendValue = value.trim() ? value : props.defalutValue
    props.onSend(sendValue)
    setValue("")
    setShowSuggestions(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSend()
    }
  }

  useEffect(() => {
    getSuggestions()
  }, [])

  useEffect(() => {
    if (isTyping) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      debounceRef.current = setTimeout(() => {
        getSuggestions()
      }, DEBOUNCE_DELAY)
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [value, isTyping])

  return (
    <div className="association-input-container" ref={containerRef}>
      <div className="input-wrapper">
        <span className="logo">
          {/* https://www.figma.com/design/eYAuWNzwrLPBGQfvD7x1S6/%E6%95%B0%E5%AD%97%E4%BA%BA%E4%BA%A7%E5%93%81%E7%BB%8F%E7%90%86?node-id=178-1853&t=Oq1xd2NI6HHmm9ax-0 */}
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M13.1962 14.1044L14.5447 3.86743H17.0866L15.7381 14.1044H13.1962ZM9.1901 9.84868H7.22687L10.1976 5.69868H10.2485L8.92922 14.108L11.3536 14.108L12.8267 3.87102L8.81141 3.87103L1.63843 14.0949H4.55508L6.17643 11.745H8.81141L9.1901 9.84868Z"
              fill="url(#paint0_linear_178_1935)"
              fillOpacity="0.9"
            />
            <path
              d="M2.60453 2.31726L2.86349 3.01709C3.13461 3.74978 3.7123 4.32745 4.44499 4.59857L5.14484 4.85753L4.44499 5.1165C3.7123 5.38761 3.13461 5.96529 2.86349 6.69798L2.60453 7.39781L2.34556 6.69798C2.07444 5.96529 1.49675 5.38761 0.764061 5.1165L0.064209 4.85753L0.764061 4.59857C1.49675 4.32745 2.07444 3.74978 2.34556 3.01709L2.60453 2.31726Z"
              fill="url(#paint1_linear_178_1935)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_178_1935"
                x1="2.16373"
                y1="11.5079"
                x2="14.9757"
                y2="2.46988"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#5498FF" />
                <stop offset="0.333333" stopColor="#7C74FF" />
                <stop offset="0.666667" stopColor="#F16F9E" />
                <stop offset="1" stopColor="#FFD98E" />
              </linearGradient>
              <linearGradient
                id="paint1_linear_178_1935"
                x1="-3.39652"
                y1="8.69511"
                x2="1.19499"
                y2="1.41639"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#FFC047" />
                <stop offset="0.333333" stopColor="#F16F9E" />
                <stop offset="0.470074" stopColor="#5498FF" />
                <stop offset="1" stopColor="#7C74FF" />
              </linearGradient>
            </defs>
          </svg>
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => {
            if (props.status !== "disabled") {
              setValue(e.target.value)
              setShowSuggestions(true)
              setIsTyping(true)
            }
          }}
          onFocus={() => {
            if (props.status !== "disabled") {
              setShowSuggestions(true)
              setIsTyping(true)
            }
          }}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          onKeyDown={handleKeyDown}
          placeholder={props.defalutValue}
          disabled={props.status === "disabled"}
        />
        <button
          className="send-button"
          onClick={handleSend}
          disabled={props.status === "disabled"}
        >
          <span>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="24" height="24" rx="6" fill="#2682FA" />
              <path
                d="M17.1012 6.21926C17.4794 6.1049 17.8326 6.45806 17.7182 6.83632L14.2223 18.3999C14.114 18.758 13.6629 18.8712 13.3984 18.6067L10.7171 15.9254C10.4576 15.6659 10.4007 15.2661 10.5776 14.9445L12.2393 11.9226C12.3189 11.7778 12.16 11.6189 12.0153 11.6985L8.99296 13.36C8.67137 13.5368 8.27165 13.4799 8.01215 13.2204L5.33078 10.5391C5.06626 10.2745 5.17947 9.82349 5.53755 9.71523L17.1012 6.21926Z"
                fill="white"
              />
            </svg>
          </span>
        </button>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="suggestions-list">
          <li className="suggestion-title">猜你想送</li>
          {suggestions.map((suggestion, index) => (
            <li key={index} onClick={() => handleSelect(suggestion)}>
              <HighlightMatch text={suggestion} query={value} />
            </li>
          ))}
        </ul>
      )}

      {props.status === "loading" && (
        <div className="loading-indicator">Loading...</div>
      )}
      {props.status === "error" && (
        <div className="error-message">Failed to load suggestions</div>
      )}
    </div>
  )
}
